import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';
import { Icons } from '@bbkAdminUtils/icons';

enum ButtonVariant {
  brand,
  plain,
}

type ButtonProps = {
  id?: string;
  isLoading?: boolean;
  variant?: keyof typeof ButtonVariant;
  trackId: string;
  trackPayload: Record<string, unknown>;
  onClick?: () => void;
} & React.ComponentPropsWithRef<'button'>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      children,
      className,
      disabled: buttonDisabled,
      isLoading,
      variant = 'brand',
      trackId,
      trackPayload,
      onClick,
      ...rest
    },
    ref
  ) => {
    const disabled = isLoading ?? buttonDisabled;

    const handleOnClick = () => {
      onClick?.();
    };

    return (
      <button
        id={id}
        data-testid={`button--${id}`}
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={handleOnClick}
        className={cs(
          'tw-inline-flex tw-items-center tw-rounded-pricingtable-border-radius tw-px-6 tw-py-4 tw-font-semibold tw-leading-6',
          'focus:outline-none focus-visible:tw-ring',
          'tw-shadow-sm',
          'tw-transition-colors tw-duration-75',
          //#region  //*=========== Variants ===========
          [
            variant === 'brand' && [
              'tw-shadow-base tw-rounded-pricingtable-border-radius tw-text-center tw-text-sm tw-font-semibold @md:tw-text-sm',
              'tw-bg-[var(--pricing-page-button-bg-color)] tw-text-[var(--pricing-page-button-text-color)]',
              'tw-transition-all tw-duration-200',
              'hover:tw-scale-[1.015] hover:tw-shadow-lg hover:tw-brightness-105',
            ],
            variant === 'plain' && [
              'tw-text-[var(--pricing-page-text-font-text-color)]',
              'tw-shadow-none',
              'tw-px-2 tw-py-1',
              'hover:tw-opacity-80',
              'active:tw-scale-95',
              'tw-transition-transform tw-duration-100',
            ],
          ],
          //#endregion  //*======== Variants ===========
          'disabled:cursor-not-allowed',
          isLoading &&
            'tw-relative tw-text-transparent tw-transition-none hover:tw-text-transparent disabled:tw-cursor-wait',
          className
        )}
        {...rest}
      >
        <div
          className={cs(
            'tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2',
            !isLoading && 'tw-hidden',
            {
              'tw-text-[var(--pricing-page-button-text-color)]': [
                'brand',
              ].includes(variant),
              'tw-text-[var(--pricing-page-text-font-text-color)]': [
                'plain',
              ].includes(variant),
            }
          )}
        >
          {Icons.loaderSW6}
        </div>
        {children}
      </button>
    );
  }
);

export default Button;
